import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import Footer from '../components/Footer';
import * as api from '../services/api';
import { useCart } from '../contexts/CartContext';
import './CategoryPage.css';

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // Store all products
  const [products, setProducts] = useState([]); // Displayed products
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    loadCategoryData();
  }, [categorySlug]);

  // Extract unique subcategories from products
  const extractSubcategoriesFromProducts = (productsList, categoryData = null) => {
    const subcatMap = new Map();
    
    // First, extract subcategories from actual products
    productsList.forEach(product => {
      if (product.subcategory && product.subcategory.trim()) {
        const subcatName = product.subcategory.trim();
        if (!subcatMap.has(subcatName)) {
          subcatMap.set(subcatName, {
            name: subcatName,
            slug: subcatName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            count: 0
          });
        }
        subcatMap.get(subcatName).count++;
      }
    });

    // Also include subcategories from category definition (if they exist and not already in map)
    if (categoryData && categoryData.subcategories) {
      categoryData.subcategories.forEach(subcat => {
        const subcatName = subcat.name || subcat;
        if (!subcatMap.has(subcatName)) {
          subcatMap.set(subcatName, {
            name: subcatName,
            slug: (subcat.slug || subcatName.toLowerCase().replace(/[^a-z0-9]+/g, '-')),
            count: 0
          });
        }
      });
    }

    return Array.from(subcatMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      
      // Try to get category by slug first
      try {
        const foundCategory = await api.getCategory(categorySlug);
        setCategory(foundCategory);
        
        // Load products for this category using category ID
        const categoryProducts = await api.getProducts({ category: foundCategory._id });
        console.log('Loaded products:', categoryProducts.length, 'for category:', foundCategory.name, 'ID:', foundCategory._id);
        
        setAllProducts(categoryProducts);
        
        // Extract subcategories from actual products (pass category data)
        const extractedSubcategories = extractSubcategoriesFromProducts(categoryProducts, foundCategory);
        setSubcategories(extractedSubcategories);
        
        // Show all products initially
        setProducts(categoryProducts);
        setSelectedSubcategory(null);
      } catch (error) {
        console.error('Error getting category by slug:', error);
        // If category not found by slug, try to find by name match or slug variations
        const categories = await api.getCategories();
        const normalizedSlug = categorySlug.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        
        const foundCategory = categories.find(cat => {
          const catSlug = (cat.slug || '').toLowerCase();
          const catNameSlug = (cat.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
          return catSlug === normalizedSlug || 
                 catNameSlug === normalizedSlug ||
                 catSlug === categorySlug.toLowerCase() ||
                 cat.name.toLowerCase() === categorySlug.toLowerCase();
        });
        
        if (foundCategory) {
          setCategory(foundCategory);
          const categoryProducts = await api.getProducts({ category: foundCategory._id });
          console.log('Loaded products:', categoryProducts.length, 'for category:', foundCategory.name, 'ID:', foundCategory._id);
          
          setAllProducts(categoryProducts);
          
          // Extract subcategories from actual products (pass category data)
          const extractedSubcategories = extractSubcategoriesFromProducts(categoryProducts, foundCategory);
          setSubcategories(extractedSubcategories);
          
          // Show all products initially
          setProducts(categoryProducts);
          setSelectedSubcategory(null);
        } else {
          // Try to load products by subcategory name
          const categoryProducts = await api.getProducts({ subcategory: categorySlug });
          console.log('Loaded products by subcategory:', categoryProducts.length);
          setAllProducts(categoryProducts);
          setProducts(categoryProducts);
          
          // Extract subcategories from products
          const extractedSubcategories = extractSubcategoriesFromProducts(categoryProducts, null);
          setSubcategories(extractedSubcategories);
        }
      }
    } catch (error) {
      console.error('Error loading category:', error);
      setProducts([]);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubcategoryClick = (subcategoryName) => {
    setSelectedSubcategory(subcategoryName);
    
    if (!subcategoryName) {
      // Show all products
      setProducts(allProducts);
    } else {
      // Filter products by subcategory
      const filtered = allProducts.filter(product => 
        product.subcategory && product.subcategory.trim() === subcategoryName
      );
      console.log('Filtered products:', filtered.length, 'for subcategory:', subcategoryName);
      setProducts(filtered);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="category-page-container">
      <div className="category-page-header">
        <button 
          onClick={() => navigate('/')} 
          className="back-button"
        >
          ← Back to Home
        </button>
        {category && (
          <h1 className="category-title">{category.name}</h1>
        )}
      </div>

      {/* Mobile Subcategory Navigation - Horizontal at top */}
      {subcategories.length > 0 && (
        <nav className="mobile-subcategory-nav">
          <div className="mobile-subcategory-list">
            <button
              className={`mobile-subcategory-item ${selectedSubcategory === null ? 'active' : ''}`}
              onClick={() => handleSubcategoryClick(null)}
            >
              <div className="mobile-subcategory-icon">
                <span>📦</span>
              </div>
              <span className="mobile-subcategory-name">All ({allProducts.length})</span>
            </button>
            {subcategories.map((subcat) => (
              <button
                key={subcat.slug || subcat.name}
                className={`mobile-subcategory-item ${selectedSubcategory === subcat.name ? 'active' : ''}`}
                onClick={() => handleSubcategoryClick(subcat.name)}
              >
                <div className="mobile-subcategory-icon">
                  {subcat.image ? (
                    <img 
                      src={subcat.image} 
                      alt={subcat.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.onerror = null; // Stop retrying
                      }}
                    />
                  ) : (
                    <span>📦</span>
                  )}
                </div>
                <span className="mobile-subcategory-name">{subcat.name} ({subcat.count})</span>
              </button>
            ))}
          </div>
        </nav>
      )}

      <div className="category-page-layout">
        {/* Left Sidebar - Subcategories (Desktop only) */}
        {subcategories.length > 0 && (
          <aside className="category-sidebar">
            <div className="subcategory-list">
              <button
                className={`subcategory-item ${selectedSubcategory === null ? 'active' : ''}`}
                onClick={() => handleSubcategoryClick(null)}
              >
                <div className="subcategory-icon">📦</div>
                <span>All ({allProducts.length})</span>
              </button>
              {subcategories.map((subcat) => (
                <button
                  key={subcat.slug || subcat.name}
                  className={`subcategory-item ${selectedSubcategory === subcat.name ? 'active' : ''}`}
                  onClick={() => handleSubcategoryClick(subcat.name)}
                >
                  <div className="subcategory-icon">
                    {subcat.image ? (
                      <img 
                        src={subcat.image} 
                        alt={subcat.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.onerror = null; // Stop retrying
                        }}
                      />
                    ) : (
                      <span>📦</span>
                    )}
                  </div>
                  <span>{subcat.name} ({subcat.count})</span>
                </button>
              ))}
            </div>
          </aside>
        )}

        {/* Right Side - Products Grid */}
        <main className="category-products-section">
          {products.length > 0 ? (
            selectedSubcategory ? (
              // Show filtered products when a subcategory is selected (no header, just products)
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard 
                    key={product._id} 
                    product={product} 
                    onAddToCart={addToCart}
                  />
                ))}
              </div>
            ) : (
              // Show all products in a single grid when "All" is selected (no subcategory headers)
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard 
                    key={product._id} 
                    product={product} 
                    onAddToCart={addToCart}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="empty-state">
              <p>No products found {selectedSubcategory ? `in ${selectedSubcategory}` : 'in this category'}.</p>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}

